import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  View,
  ViewStyle,
} from 'react-native';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import theme from '@themes/theme';

export type TooltipOnboardingVariant =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
  | 'left-top'
  | 'left-center'
  | 'left-bottom'
  | 'right-top'
  | 'right-center'
  | 'right-bottom';

export type TooltipOnboardingSize = 'sm' | 'md' | 'lg';
export type TooltipOnboardingStyle = 'default' | 'success' | 'custom';

export interface TooltipOnboardingProps {
  variant?: TooltipOnboardingVariant;
  size?: TooltipOnboardingSize;
  tooltipStyle?: TooltipOnboardingStyle;
  content?: string | React.ReactNode;

  // Rich content props (from Figma)
  title?: string;
  description?: string;
  step?: string;
  ctaText?: string;
  onCtaPress?: () => void;
  avatar?: React.ReactNode;

  children: React.ReactNode;
  forceShow?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  style?: ViewStyle;
  animatedOpacity?: any; // To support Animated.Value fading
  trigger?: 'hover' | 'press';
  offset?: number; // Gap in px between trigger and tooltip (default 20)
  horizontalOffset?: number; // Shift portal left/right in px (positive = right)
  arrowAtTriggerCenter?: boolean; // Shift balloon so left-arrow aligns with trigger center
  triggerAnchorHeight?: number; // Use a fixed trigger height for vertical centering when child has helper/error text
  externalTriggerRef?: React.RefObject<any>; // Use an external element as positioning anchor instead of the wrapper Pressable
}

const sizeConfig = {
  sm: { paddingVertical: 12, paddingHorizontal: 12, fontSize: 12 },
  md: { paddingVertical: 16, paddingHorizontal: 16, fontSize: 14 },
  lg: { paddingVertical: 16, paddingHorizontal: 20, fontSize: 16 },
};

function getArrowStyle(
  variant: TooltipOnboardingVariant,
  bgColor: string
): ViewStyle {
  const size = 24;
  const offset = -(size / 2);
  let radiusStyle: ViewStyle = {};
  if (variant.startsWith('top')) {
    radiusStyle = { borderBottomRightRadius: 4 }; // points DOWN
  } else if (variant.startsWith('bottom')) {
    radiusStyle = { borderTopLeftRadius: 4 }; // points UP
  } else if (variant.startsWith('left')) {
    radiusStyle = { borderTopRightRadius: 4 }; // points RIGHT
  } else if (variant.startsWith('right')) {
    radiusStyle = { borderBottomLeftRadius: 4 }; // points LEFT
  }

  const baseStyle: ViewStyle = {
    position: 'absolute',
    width: size,
    height: size,
    backgroundColor: bgColor,
    ...radiusStyle,
    transform: [{ rotate: '45deg' }],
  };

  switch (variant) {
    case 'top-left':
      return { ...baseStyle, bottom: offset, left: 16 };
    case 'top-center':
      return { ...baseStyle, bottom: offset, left: '50%', marginLeft: offset };
    case 'top-right':
      return { ...baseStyle, bottom: offset, right: 16 };

    case 'bottom-left':
      return { ...baseStyle, top: offset, left: 16 };
    case 'bottom-center':
      return { ...baseStyle, top: offset, left: '50%', marginLeft: offset };
    case 'bottom-right':
      return { ...baseStyle, top: offset, right: 16 };

    case 'left-top':
      return { ...baseStyle, right: offset, top: 16 };
    case 'left-center':
      return { ...baseStyle, right: offset, top: '50%', marginTop: offset };
    case 'left-bottom':
      return { ...baseStyle, right: offset, bottom: 16 };

    case 'right-top':
      return { ...baseStyle, left: offset, top: 16 };
    case 'right-center':
      return { ...baseStyle, left: offset, top: '50%', marginTop: offset };
    case 'right-bottom':
      return { ...baseStyle, left: offset, bottom: 16 };

    default:
      return { ...baseStyle, bottom: offset, left: '50%', marginLeft: offset };
  }
}

function ContentNode({
  content,
  fontSize,
}: {
  content: React.ReactNode;
  fontSize: number;
}) {
  if (typeof content === 'string') {
    return (
      <Text color='white' style={{ fontSize, lineHeight: fontSize * 1.5 }}>
        {content}
      </Text>
    );
  }
  return <>{content}</>;
}

function RichTooltipContent({
  title,
  description,
  step,
  ctaText,
  onCtaPress,
  content,
  size,
  variant,
  bgColor,
  avatar,
}: any) {
  const currentSize = sizeConfig[size as TooltipOnboardingSize];

  return (
    <Box
      borderRadius='m'
      p='m'
      width={312}
      style={{
        backgroundColor: bgColor,
        boxShadow: '0px 5px 12.5px rgba(0,0,0,0.05)',
      }}>
      <View style={getArrowStyle(variant, bgColor)} />

      {content && !title && !description ? (
        <ContentNode content={content} fontSize={currentSize.fontSize} />
      ) : (
        <Box gap='m'>
          <Box flexDirection='row' alignItems='flex-start' style={{ gap: 10 }}>
            {avatar && <Box style={{ flexShrink: 0 }}>{avatar}</Box>}
            <Box style={{ flex: 1, gap: 6 }}>
              {title && (
                <Text color='white' variant='labelEmphasizedChatDetialTitle'>
                  {title}
                </Text>
              )}
              {description && (
                <Text color='white' variant='webBodySecondary'>
                  {description}
                </Text>
              )}
            </Box>
          </Box>

          {(step || ctaText) && (
            <Box
              flexDirection='row'
              alignItems='center'
              justifyContent='space-between'
              gap='m'>
              <Box flex={1}>
                {step && (
                  <Text color='grey01' variant='webBodySecondary'>
                    {step}
                  </Text>
                )}
              </Box>
              {ctaText && (
                <Button
                  isSmall={true}
                  borderRadius='m'
                  backgroundColor='black'
                  onPress={onCtaPress}
                  height={32}>
                  <Text color='white' variant='buttonLabel'>
                    {ctaText}
                  </Text>
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export const TooltipOnboardingWeb = ({
  variant = 'top-center',
  size = 'md',
  tooltipStyle = 'default',
  content,
  title,
  description,
  step,
  ctaText,
  onCtaPress,
  avatar,
  children,
  forceShow = false,
  open,
  onOpenChange,
  disabled = false,
  style,
  animatedOpacity,
  trigger = 'hover',
  offset,
  horizontalOffset = 0,
  arrowAtTriggerCenter = false,
  triggerAnchorHeight,
  externalTriggerRef,
}: TooltipOnboardingProps) => {
  const isControlled = open !== undefined;
  const [internalVisible, setInternalVisible] = useState(false);
  const isVisible = isControlled ? open! : internalVisible;

  const setIsVisible = (val: boolean) => {
    if (!isControlled) setInternalVisible(val);
    onOpenChange?.(val);
  };

  const [layout, setLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const triggerRef = useRef<any>(null);
  const [portalRect, setPortalRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [triggerInViewport, setTriggerInViewport] = useState(true);
  const enterOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const activeRef = externalTriggerRef ?? triggerRef;
    if (!(forceShow || isVisible) || !activeRef.current) return;

    const getEl = () => {
      const el = (externalTriggerRef?.current ?? triggerRef.current) as any;
      const node = el?._nativeTag ? null : el;
      return node ?? (el?.getDOMNode ? el.getDOMNode() : null);
    };

    const measure = () => {
      const domEl = getEl();
      if (domEl?.getBoundingClientRect) {
        const rect = domEl.getBoundingClientRect();
        setPortalRect((prev) => {
          if (
            !prev ||
            prev.top !== rect.top ||
            prev.left !== rect.left ||
            prev.width !== rect.width ||
            prev.height !== rect.height
          ) {
            return {
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            };
          }
          return prev;
        });
      }
    };

    measure();

    const domEl = getEl();
    let resizeObserver: ResizeObserver | null = null;
    if (domEl && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(domEl);
    }

    let intersectionObserver: IntersectionObserver | null = null;
    if (domEl && typeof IntersectionObserver !== 'undefined') {
      intersectionObserver = new IntersectionObserver(
        ([entry]) => setTriggerInViewport(entry.isIntersecting),
        { threshold: 0 }
      );
      intersectionObserver.observe(domEl);
    }

    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    return () => {
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [forceShow, isVisible, externalTriggerRef]);

  const handlePressIn = () => {
    if (!disabled && trigger !== 'press') setIsVisible(true);
  };
  const handlePressOut = () => {
    if (!disabled && trigger !== 'press') setIsVisible(false);
  };
  const handlePress = () => {
    if (!disabled && trigger === 'press') setIsVisible(!isVisible);
  };
  const handleMouseEnter = () => {
    if (!disabled && trigger !== 'press') setIsVisible(true);
  };
  const handleMouseLeave = () => {
    if (!disabled && trigger !== 'press') setIsVisible(false);
  };

  const handleLayout = (event: any) => {
    const { x, y, width, height } = event.nativeEvent.layout;
    setLayout({ x, y, width, height });
  };

  const shouldShow = (forceShow || isVisible) && !disabled;

  useEffect(() => {
    if (shouldShow) {
      enterOpacity.setValue(0);
      Animated.timing(enterOpacity, {
        toValue: 1,
        duration: 280,
        delay: 80,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    } else {
      enterOpacity.setValue(0);
    }
  }, [shouldShow]);

  let backgroundColor = theme.colors.black;
  if (tooltipStyle === 'success') {
    backgroundColor = theme.colors.greenSecondary;
  } else if (tooltipStyle === 'custom') {
    backgroundColor = 'transparent';
  }

  const getTooltipPositionNative = (): ViewStyle => {
    const offset = 20;
    const anchorHeight = triggerAnchorHeight ?? layout.height;
    switch (variant) {
      case 'top-left':
        return { bottom: layout.height + offset, left: 0 };
      case 'top-center':
        return {
          bottom: layout.height + offset,
          left: '50%',
          transform: [{ translateX: '-50%' as any }],
        };
      case 'top-right':
        return { bottom: layout.height + offset, right: 0 };
      case 'bottom-left':
        return { top: layout.height + offset, left: 0 };
      case 'bottom-center':
        return {
          top: layout.height + offset,
          left: '50%',
          transform: [{ translateX: '-50%' as any }],
        };
      case 'bottom-right':
        return { top: layout.height + offset, right: 0 };
      case 'left-top':
        return { right: layout.width + offset, top: 0 };
      case 'left-center':
        return {
          right: layout.width + offset,
          top: anchorHeight / 2,
          transform: [{ translateY: '-50%' as any }],
        };
      case 'left-bottom':
        return { right: layout.width + offset, bottom: 0 };
      case 'right-top':
        return { left: layout.width + offset, top: 0 };
      case 'right-center':
        return {
          left: layout.width + offset,
          top: anchorHeight / 2,
          transform: [{ translateY: '-50%' as any }],
        };
      case 'right-bottom':
        return { left: layout.width + offset, bottom: 0 };
      default:
        return {
          bottom: layout.height + offset,
          left: '50%',
          transform: [{ translateX: '-50%' as any }],
        };
    }
  };

  const isRich = !!title || !!description || !!step || !!ctaText;

  const renderTooltipContent = () => {
    if (isRich) {
      return (
        <RichTooltipContent
          title={title}
          description={description}
          step={step}
          ctaText={ctaText}
          onCtaPress={onCtaPress}
          content={content}
          size={size}
          variant={variant}
          bgColor={backgroundColor}
          avatar={avatar}
        />
      );
    }

    // Simple Tooltip logic
    const currentSize = sizeConfig[size];
    return (
      <View
        style={{
          backgroundColor,
          borderRadius: tooltipStyle === 'custom' ? 0 : 16,
          paddingVertical:
            tooltipStyle === 'custom' ? 0 : currentSize.paddingVertical,
          paddingHorizontal:
            tooltipStyle === 'custom' ? 0 : currentSize.paddingHorizontal,
          maxWidth: 200,
          ...Platform.select({
            web: { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } as any,
            default: {
              elevation: 5,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 4,
            },
          }),
        }}>
        <View style={getArrowStyle(variant, backgroundColor)} />
        {typeof content === 'string' ? (
          <Text
            style={{
              color: theme.colors.white,
              fontSize: currentSize.fontSize,
              fontFamily: 'Inter_400Regular',
              lineHeight: currentSize.fontSize * 1.5,
            }}>
            {content}
          </Text>
        ) : (
          content
        )}
      </View>
    );
  };

  const renderWebPortal = () => {
    if (!shouldShow || !portalRect || !triggerInViewport) return null;
    const gap = offset ?? 20;
    const anchorHeight = triggerAnchorHeight ?? portalRect.height;
    let top = 0,
      left = 0;
    let transform = '';
    switch (variant) {
      case 'top-left':
        top = portalRect.top - gap;
        left = portalRect.left;
        transform = 'translateY(-100%)';
        break;
      case 'top-center':
        top = portalRect.top - gap;
        left = portalRect.left + portalRect.width / 2;
        transform = 'translate(-50%, -100%)';
        break;
      case 'top-right':
        top = portalRect.top - gap;
        left = portalRect.left + portalRect.width;
        transform = 'translate(-100%, -100%)';
        break;
      case 'bottom-left':
        top = portalRect.top + portalRect.height + gap;
        left = portalRect.left;
        break;
      case 'bottom-center':
        top = portalRect.top + portalRect.height + gap;
        left = portalRect.left + portalRect.width / 2;
        transform = 'translateX(-50%)';
        break;
      case 'bottom-right':
        top = portalRect.top + portalRect.height + gap;
        left = portalRect.left + portalRect.width;
        transform = 'translateX(-100%)';
        break;
      case 'left-top':
        top = portalRect.top;
        left = portalRect.left - gap;
        transform = 'translateX(-100%)';
        break;
      case 'left-center':
        top = portalRect.top + anchorHeight / 2;
        left = portalRect.left - gap;
        transform = 'translate(-100%, -50%)';
        break;
      case 'left-bottom':
        top = portalRect.top + portalRect.height;
        left = portalRect.left - gap;
        transform = 'translate(-100%, -100%)';
        break;
      case 'right-top':
        top = portalRect.top;
        left = portalRect.left + portalRect.width + gap;
        break;
      case 'right-center':
        top = portalRect.top + anchorHeight / 2;
        left = portalRect.left + portalRect.width + gap;
        transform = 'translateY(-50%)';
        break;
      case 'right-bottom':
        top = portalRect.top + portalRect.height;
        left = portalRect.left + portalRect.width + gap;
        transform = 'translateY(-100%)';
        break;
      default:
        top = portalRect.top - gap;
        left = portalRect.left + portalRect.width / 2;
        transform = 'translate(-50%, -100%)';
    }

    // Shift balloon so arrow aligns with trigger center
    if (arrowAtTriggerCenter) {
      if (variant === 'top-left') {
        // arrow left:16, center = 28px from balloon left; shift so arrow center = trigger center
        left = portalRect.left + portalRect.width / 2 - 28;
        transform = 'translateY(-100%)';
      } else if (variant === 'bottom-left' || variant === 'bottom-center') {
        // arrow left:16, center = left+12 = left+28 from element edge → shift so arrow center = trigger center
        left = portalRect.left + portalRect.width / 2 - 28;
        transform = '';
      } else if (variant === 'bottom-right') {
        // arrow right:16, center = 28px from right edge; translateX(-100%) so right edge = css left
        // arrow center (absolute) = css_left - 28 → css_left = triggerCenter + 28
        left = portalRect.left + portalRect.width / 2 + 28;
        transform = 'translateX(-100%)';
      }
    }

    left += horizontalOffset;
    const { createPortal } = require('react-dom');
    const displayOpacity =
      animatedOpacity !== undefined
        ? Animated.multiply(animatedOpacity as any, enterOpacity)
        : enterOpacity;
    return createPortal(
      <Animated.View
        style={{
          position: 'fixed' as any,
          top,
          left,
          transform: transform as any,
          zIndex: 99999,
          opacity: displayOpacity as any,
          ...(trigger === 'hover' && !ctaText && !onCtaPress
            ? { pointerEvents: 'none' }
            : {}),
        }}>
        {renderTooltipContent()}
      </Animated.View>,
      document.body
    );
  };

  return (
    <View
      style={[
        {
          position: 'relative',
          zIndex: shouldShow ? 99999 : 1,
          alignSelf: 'flex-start',
        },
        style,
      ]}>
      <Pressable
        ref={triggerRef}
        onPress={handlePress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onLayout={handleLayout}
        style={style && 'flex' in style ? { flex: style.flex } : undefined}
        {...(Platform.OS === 'web'
          ? {
              onMouseEnter: handleMouseEnter,
              onMouseLeave: handleMouseLeave,
              style: {
                cursor: 'pointer',
                ...(style && 'flex' in style ? { flex: style.flex } : {}),
              } as any,
            }
          : {})}>
        {children}
      </Pressable>

      {Platform.OS === 'web' && renderWebPortal()}

      {Platform.OS !== 'web' && shouldShow && (
        <Animated.View
          style={[
            {
              position: 'absolute',
              zIndex: 99999,
              opacity:
                animatedOpacity !== undefined
                  ? (Animated.multiply(
                      animatedOpacity as any,
                      enterOpacity
                    ) as any)
                  : enterOpacity,
            },
            getTooltipPositionNative(),
          ]}
          pointerEvents={trigger === 'hover' ? 'none' : 'auto'}>
          {renderTooltipContent()}
        </Animated.View>
      )}
    </View>
  );
};
